import type { SessionUser } from '@afra/shared'
import { useState } from 'react'
import { AccountsTab } from './AccountsTab'
import { OrgsTab } from './OrgsTab'
import { PortalTab } from './PortalTab'
import { SettingsTab } from './SettingsTab'
import { StaffTab } from './StaffTab'
import { SentimentTab } from './SentimentTab'
import { TrailTab } from './TrailTab'

type Tab = 'orgs' | 'accounts' | 'staff' | 'sentiment' | 'trail' | 'settings'

const TABS: { id: Tab; labelFa: string }[] = [
  { id: 'orgs', labelFa: 'مؤسسه‌ها' },
  { id: 'accounts', labelFa: 'حساب‌ها' },
  { id: 'staff', labelFa: 'کارشناسان افرا' },
  { id: 'sentiment', labelFa: 'رضایت از گفت‌وگو' },
  { id: 'trail', labelFa: 'سابقهٔ دسترسی‌ها' },
  { id: 'settings', labelFa: 'تنظیمات' },
]

/**
 * The staff surface.
 *
 * An `afra_admin` sees all of it and can change none of it; a `super_admin`
 * gets the actions. The read/write split is enforced on the server — this only
 * decides whether a button that would be refused is drawn at all.
 */
export function PlatformPage({ user }: { user: SessionUser }) {
  const [tab, setTab] = useState<Tab>('orgs')
  /* Two different populations, and conflating them is what makes the platform
     look empty: nine organizations have an account here, and about two thousand
     charities are in Afra's records. */
  const [orgView, setOrgView] = useState<'tenants' | 'portal'>('tenants')
  const canManage = user.role === 'super_admin'

  return (
    <div className="container container--wide stack">
      <div>
        <h1>مدیریت سامانه</h1>
        <p className="meta">
          {canManage
            ? 'مؤسسه‌ها، حساب‌ها و اینکه چه کسی به کجا دسترسی دارد. می‌توانید به‌جای هر مؤسسه هم وارد شوید.'
            : 'مؤسسه‌ها و حساب‌ها را می‌بینید. تغییر دادنشان از عهدهٔ مدیر ارشد برمی‌آید.'}
        </p>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tab ${tab === t.id ? 'is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.labelFa}
          </button>
        ))}
      </div>

      {tab === 'orgs' && (
        <div className="stack">
          <div className="row">
            <button
              type="button"
              className={`btn btn--sm ${orgView === 'tenants' ? '' : 'btn--quiet'}`}
              onClick={() => setOrgView('tenants')}
            >
              مؤسسه‌های دارای حساب
            </button>
            <button
              type="button"
              className={`btn btn--sm ${orgView === 'portal' ? '' : 'btn--quiet'}`}
              onClick={() => setOrgView('portal')}
            >
              همهٔ مؤسسه‌های افرا
            </button>
          </div>

          {orgView === 'tenants' ? (
            <OrgsTab canImpersonate={canManage} />
          ) : (
            <PortalTab canManage={canManage} />
          )}
        </div>
      )}
      {tab === 'accounts' && <AccountsTab canManage={canManage} />}
      {tab === 'staff' && <StaffTab canManage={canManage} />}
      {tab === 'sentiment' && <SentimentTab />}
      {tab === 'trail' && <TrailTab />}
      {tab === 'settings' && <SettingsTab canManage={canManage} canManageBehaviors={user.role === 'afra_admin' || canManage} />}
    </div>
  )
}
